import {
  mdiAlert,
  mdiChevronDown,
  mdiClockOutline,
  mdiPackageCheck,
  mdiUndo,
} from "@mdi/js";
import Icon from "@mdi/react";
import React, { useState } from "react";
import "../inward-styles.css";
import { DisputedItemModel } from "../model/quarantine_model";

interface PastDisputesProps {
  disputes: DisputedItemModel[];
}

const statusConfig: Record<
  string,
  { label: string; colorClass: string; icon: string }
> = {
  open: {
    label: "Dispute Active",
    colorClass: "bg-warning/15 text-warning border-warning/30",
    icon: mdiClockOutline,
  },
  returned: {
    label: "Returned to Vendor",
    colorClass: "bg-blue-50 text-blue-700 border-blue-200",
    icon: mdiUndo,
  },
  resolved: {
    label: "Dispute Resolved",
    colorClass: "bg-green-50 text-green-700 border-green-200",
    icon: mdiPackageCheck,
  },
};

const typeLabels: Record<string, string> = {
  quantity_mismatch: "Qty Mismatch",
  quality_issue: "Quality Issue",
  documentation_issue: "Doc Issue",
  rate_mismatch: "Rate Mismatch",
};

const PastDisputes: React.FC<PastDisputesProps> = ({ disputes }) => {
  const [open, setOpen] = useState(false);

  if (!disputes || disputes.length === 0) return null;

  const activeCount = disputes.filter((d) => d.status === "open").length;

  return (
    <div className="space-y-3">
      {/* Trigger */}
      <button
        id="warehouse-inward-past-disputes-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-lg border border-yellow-400 bg-yellow-50 p-2 text-left transition-all hover:bg-yellow-100/50"
      >
        <div className="flex items-center gap-2">
          <Icon path={mdiAlert} size={0.7} className="text-yellow-600" />
          <span className="text-xs font-bold text-gray-900">
            Past Disputes ({disputes.length})
          </span>
          {activeCount > 0 && (
            <span className="inline-flex items-center rounded-full bg-yellow-200 px-2 py-0.5 text-xs font-bold text-yellow-800">
              {activeCount} Active
            </span>
          )}
        </div>
        <Icon
          path={mdiChevronDown}
          size={0.7}
          className={`text-gray-500 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Content */}
      {open && (
        <div
          id="warehouse-inward-past-disputes-content"
          className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200 pl-4"
        >
          {disputes.map((dispute) => {
            const config = statusConfig[dispute.status] || statusConfig.open;
            return (
              <div
                key={dispute.id}
                className="flex items-center justify-between gap-3 rounded-md border bg-white p-2.5 text-xs shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-gray-900 truncate uppercase tracking-tight">
                      {dispute.item_name}
                    </span>
                    <span className="text-gray-500 text-xs px-1">
                      ( {dispute.inventory_item_code} )
                    </span>
                    <span className="text-xs font-bold text-blue-700 px-1 py-0.5">
                      {dispute.disputed_physical_quantity} units
                    </span>
                    {dispute.disputed_invoice_rate > 0 && (
                      <span className="text-xs font-bold text-slate-700 px-1 py-0.5 border-l border-gray-300 ml-1">
                        ₹{dispute.disputed_invoice_rate.toLocaleString()}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {dispute.dispute_types.map((type) => (
                      <span
                        key={type}
                        className="inline-flex items-center rounded-full border bg-gray-50 px-2 py-0 text-[9px] font-bold text-gray-600 border-gray-200"
                      >
                        {typeLabels[type] || type}
                      </span>
                    ))}
                    {dispute.remark && (
                      <span className="text-xs text-gray-500 truncate italic bg-slate-50 px-2">
                        "{dispute.remark}"
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${config.colorClass}`}
                  >
                    <Icon path={config.icon} size={0.4} />
                    {config.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PastDisputes;
